import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import AppContext from "../../AppContext";
import ShoppingContext from "../../contexts/ShoppingContext";

import { ReactComponent as CloseSvg } from "../../assets/icons/close-primary.svg";
import { Form, Formik } from "formik";
import Button from "../common/Button";
import OutsideClickHandler from "react-outside-click-handler";
import ComboBox from "../common/ComboBox";

import programSketchUpImage from "../../assets/icons/programs/sketchUp.png";
import program3dsMaxImage from "../../assets/icons/programs/3dsMax.png";
import programRevitImage from "../../assets/icons/programs/autodeskRevit.png";
import programCinema4DImage from "../../assets/icons/programs/cinema4d.png";
import programUnrealImage from "../../assets/icons/programs/unreal.png";
import programAutocadImage from "../../assets/icons/programs/autocad.png";
import programArchicadImage from "../../assets/icons/programs/archicad.png";
import programRhinoImage from "../../assets/icons/programs/rhino.png";

import programVRayImage from "../../assets/icons/programs/vray.png";
import programCoronaImage from "../../assets/icons/programs/corona.png";
import programD5RenderImage from "../../assets/icons/programs/d5render.png";
import programEnscapeImage from "../../assets/icons/programs/enscape.jpg";
import programLumionImage from "../../assets/icons/programs/lumion.png";
import { SocketContext } from "../../contexts/SocketContext";

const Filter = ({ workExchangeType, onClose }) => {
  const { designStyles, getLang } = useContext(AppContext);
  const { workExchanges: workExchangeTypes } = designStyles;
  const { shoppingFilter, setShoppingFilter } = useContext(ShoppingContext);
  const formikRef = useRef();
  const [initialValues] = useState(shoppingFilter ?? {});
  const { socket } = useContext(SocketContext);

  const _availableFilters = useMemo(() => {
    if (!workExchangeTypes)
      return {
        categories: [],
        subCategories: [],
        materials: [],
        fileTypes: [],
      };

    const categories = [];
    const subCategories = [];
    const materials = [];
    const fileTypes = [];

    for (const _type of workExchangeTypes) {
      for (const ft of _type.fileTypes) {
        if (fileTypes.find((f) => f.name === ft)) {
          continue;
        }
        fileTypes.push({
          name: ft,
          value: ft,
        });
      }

      for (const _cat of _type.categories) {
        categories.push({
          name: `${_cat.name}${_cat.projects ? ` (${_cat.projects})` : ""}`,
          value: _cat._id,
        });

        for (const subcat of _cat.subs) {
          subCategories.push({
            name: `${subcat.name}${
              subcat.projects ? ` (${subcat.projects})` : ""
            }`,
            value: subcat._id,
          });

          for (const m of subcat.materials) {
            materials.push({
              name: `${m.name}${m.projects ? ` (${m.projects})` : ""}`,
              value: m._id,
            });
          }
        }
      }
    }

    return {
      categories,
      subCategories,
      materials,
      fileTypes,
    };
  }, [workExchangeTypes]);

  useEffect(() => {
    const interval = setInterval(() => {
      init();
    }, 3000);

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    init();
  }, [workExchangeType]);

  const init = async () => {};

  const handleSubmit = (
    { categories, programs, renderers, types },
    options
  ) => {
    let _newFilter = {
      programs,
      renderers,
      types,
    };

    // * Filter all if none were selected
    if (programs.length === 0) delete _newFilter.programs;
    if (renderers.length === 0) delete _newFilter.renderers;
    if (types.length === 0) delete _newFilter.types;
    if (categories.length > 0 && workExchangeType === "3D Models") {
      _newFilter["models"] = categories;
    }

    if (
      categories.length > 0 &&
      workExchangeType === "Materials and lighting"
    ) {
      _newFilter["materials"] = categories;
    }

    if (categories.length > 0 && workExchangeType === "Drawing and block CAD") {
      _newFilter["drawings"] = categories;
    }

    setShoppingFilter(_newFilter);
    onClose();
  };

  const { categories, subs, programs, renderers, types } = initialValues;

  return (
    <Formik
      initialValues={{
        categories: categories ?? [],
        subCategories: subs ?? [],
        programs: programs ?? [],
        renderers: renderers ?? [],
        types: types ?? [],
      }}
      onSubmit={handleSubmit}
      innerRef={formikRef}
    >
      {(props) => {
        return (
          <Form className="shopping-filter">
            <OutsideClickHandler onOutsideClick={onClose}>
              <div className="shopping-filter-content">
                <div className="shopping-filter-header">
                  <h3 className="shopping-filter-header__title">Filters</h3>
                  <CloseSvg width="24" onClick={onClose} />
                </div>
                {workExchangeTypes && (
                  <>
                    <ComboBox
                      name="categories"
                      label={"Category"}
                      placeholder={"Select a category"}
                      options={_availableFilters.categories}
                      multi={true}
                    />
                    <ComboBox
                      name="subCategories"
                      label={"Sub Category"}
                      placeholder={"Select a sub category"}
                      options={_availableFilters.subCategories}
                      multi={true}
                    />
                    <ComboBox
                      name="materials"
                      label={"Materials"}
                      placeholder={"Select a material"}
                      options={_availableFilters.materials}
                      multi={true}
                    />
                  </>
                )}
                <ComboBox
                  name="programs"
                  label={"Program Used"}
                  placeholder={"Select programs"}
                  multi={true}
                  options={[
                    {
                      name: "SketchUp",
                      value: "sketchUp",
                      icon: programSketchUpImage,
                    },
                    {
                      name: "3ds Max",
                      value: "3dsMax",
                      icon: program3dsMaxImage,
                    },
                    {
                      name: "Autodesk Revit",
                      value: "revit",
                      icon: programRevitImage,
                    },
                    {
                      name: "Cinema 4D",
                      value: "cinema4D",
                      icon: programCinema4DImage,
                    },
                    {
                      name: "Unreal",
                      value: "unreal",
                      icon: programUnrealImage,
                    },
                    {
                      name: "Rhinocerus",
                      value: "rhino",
                      icon: programRhinoImage,
                    },
                    {
                      name: "Autocad",
                      value: "autocad",
                      icon: programAutocadImage,
                    },
                    {
                      name: "Archicad",
                      value: "archicad",
                      icon: programArchicadImage,
                    },
                  ]}
                />
                <ComboBox
                  name="renderers"
                  label={"Renderers"}
                  placeholder={"Select renderers"}
                  multi={true}
                  options={[
                    {
                      name: "V-Ray",
                      value: "vray",
                      icon: programVRayImage,
                    },
                    {
                      name: "Corona",
                      value: "corona",
                      icon: programCoronaImage,
                    },
                    {
                      name: "Lumion",
                      value: "lumion",
                      icon: programLumionImage,
                    },
                    {
                      name: "Enscape",
                      value: "enscape",
                      icon: programEnscapeImage,
                    },
                    {
                      name: "D5render",
                      value: "d5render",
                      icon: programD5RenderImage,
                    },
                  ]}
                />
                {workExchangeTypes && (
                  <ComboBox
                    name="types"
                    label={"File Types"}
                    placeholder={"Select Types"}
                    options={_availableFilters.fileTypes}
                    multi={true}
                  />
                )}
                <div style={{ flexGrow: 1 }} />
                <div className="shopping-filter-ctas">
                  <Button
                    label={"Clear filters"}
                    variant={"secondary"}
                    onClick={() => {
                      props.setFieldValue("categories", []);
                      props.setFieldValue("programs", []);
                      props.setFieldValue("renderers", []);
                      props.setFieldValue("types", []);
                    }}
                  />
                  <Button label={"Apply"} type="submit" />
                </div>
              </div>
            </OutsideClickHandler>
          </Form>
        );
      }}
    </Formik>
  );
};

Filter.propTypes = {};

Filter.defaultProps = {};

export default Filter;
